import { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// Common API response-time logger. The axios interceptors in AuthHelpers.setupAxios() call
// startApiTimer()/recordAxiosTiming() so every request is timed in one place — no per-call wiring.
//
// Enabled automatically in `npm run dev`. On deployed builds turn it on/off from the browser console:
//   localStorage.setItem('mb_api_timing', '1')   // on   ('0' = off, even in dev)
// Console helpers:  apiTiming.summary()  → per-endpoint avg / p95 / max table,  apiTiming.clear()

const FLAG_KEY = 'mb_api_timing';
const SLOW_MS = 1000;
const MAX_ENTRIES = 300;

// HIT = served from the local cache, MISS = cacheable but went to the network, BYPASS = caching disabled for this request
type CacheStatus = 'HIT' | 'MISS' | 'BYPASS';

export interface ApiTimingEntry {
  method: string;
  url: string;
  status: number | string;
  ms: number;
  cache?: CacheStatus;
  bytes?: number;
  params?: unknown;
}

type TimedRequestConfig = InternalAxiosRequestConfig & { __apiStart?: number };

const entries: ApiTimingEntry[] = [];

export const isApiTimingEnabled = (): boolean => {
  try {
    const flag = localStorage.getItem(FLAG_KEY);
    if (flag !== null) return flag === '1';
  } catch {
    // storage blocked — fall through to the default
  }
  return import.meta.env.DEV;
};

export const startApiTimer = (config: InternalAxiosRequestConfig): void => {
  (config as TimedRequestConfig).__apiStart = performance.now();
};

const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)}MB` : `${(bytes / 1024).toFixed(1)}KB`;

export const logApiTiming = (entry: ApiTimingEntry): void => {
  if (!isApiTimingEnabled()) return;

  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries.shift();

  const parts = [`[API] ${entry.method} ${entry.url}`, `${Math.round(entry.ms)}ms`, String(entry.status)];
  if (entry.cache) parts.push(`cache:${entry.cache}`);
  if (entry.bytes !== undefined) parts.push(formatBytes(entry.bytes));
  const line = parts.join(' | ');

  // Slow calls go through console.warn so they stand out (and can be filtered) in the console
  const log = entry.ms >= SLOW_MS ? console.warn : console.log;
  if (entry.params && Object.keys(entry.params as object).length > 0) {
    log(line, entry.params);
  } else {
    log(line);
  }
};

// Called from the axios response interceptors (success and error paths)
export const recordAxiosTiming = (
  config: InternalAxiosRequestConfig | undefined,
  status: number | string,
  response?: AxiosResponse,
): void => {
  const start = (config as TimedRequestConfig | undefined)?.__apiStart;
  if (!config || start === undefined) return;

  const method = (config.method || 'get').toUpperCase();
  let cache: CacheStatus | undefined;
  if (method === 'GET') {
    // `cached` is set by axios-cache-interceptor, which sits before us in the response chain
    cache = (response as { cached?: boolean } | undefined)?.cached
      ? 'HIT'
      : (config as { cache?: unknown }).cache === false ? 'BYPASS' : 'MISS';
  }
  const length = Number(response?.headers?.['content-length']);

  logApiTiming({
    method,
    url: config.url || '',
    status,
    ms: performance.now() - start,
    cache,
    bytes: Number.isFinite(length) && length > 0 ? length : undefined,
    params: config.params,
  });
};

export const apiTimingSummary = (): void => {
  const groups = new Map<string, ApiTimingEntry[]>();
  entries.forEach(e => {
    const key = `${e.method} ${e.url}`;
    groups.set(key, [...(groups.get(key) || []), e]);
  });

  const rows = Array.from(groups.entries()).map(([endpoint, list]) => {
    // Cache hits are near-instant and would hide the real network cost, so stats use network calls only
    const network = list.filter(e => e.cache !== 'HIT').map(e => e.ms).sort((a, b) => a - b);
    const avg = network.length ? network.reduce((sum, ms) => sum + ms, 0) / network.length : 0;
    return {
      endpoint,
      calls: list.length,
      cacheHits: list.filter(e => e.cache === 'HIT').length,
      avgMs: Math.round(avg),
      p95Ms: network.length ? Math.round(network[Math.min(network.length - 1, Math.floor(network.length * 0.95))]) : 0,
      maxMs: network.length ? Math.round(network[network.length - 1]) : 0,
    };
  });

  console.table(rows.sort((a, b) => b.avgMs - a.avgMs));
};

if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).apiTiming = {
    summary: apiTimingSummary,
    entries: () => [...entries],
    clear: () => { entries.length = 0; },
  };
}
